import * as React from 'react';
import * as DateFns from 'date-fns';
import '../css/file-upload.sass';
import ProgressBar from './ProgressBar';
import Popup from './Popup';
import Card from './Card';
import zoom from '../css/assets/img/day-ghost-zoom-inactive.svg';
import invalidImg from '../css/assets/img/invalid-img-logo.svg';
import StaticElementButton from './StaticElementButton';
import ActionPopup from './ActionPopup';
import documentImg from '../css/assets/img/icon-document.svg';

export interface FileUploadProgressProps {
  files: any;
  status: 'success' | 'error' | 'info' | 'warning' | 'highlight' | 'default';
  errorMessage: string[];
  successMessage: string;
  uploadProgress: number[];
  rejectedOn: string;
  uploadedOn: string[];
  uploadedFiles: any;
  displayRemoveButton?: boolean;
  onRemoveFile: Function;
  removeFileHeading?: string;
  removeFileMsg?: string;
  removeFileNoBtnLbl?: string;
  removeFileYesBtnLbl?: string;
  systemErrorMessage?: string;
}

interface ThumbsState {
  file: any;
  status: 'error' | 'success' | 'info' | 'warning' | 'highlight' | 'default';
  errorMessage: string;
}

export default function FileUploadProgress(props: FileUploadProgressProps) {
  const {
    removeFileHeading = 'Confirm',
    removeFileMsg = 'Are you sure you want to remove this file?',
    removeFileNoBtnLbl = 'No',
    removeFileYesBtnLbl = 'Yes',
    systemErrorMessage = 'Whoops. Something went wrong on our end. Please try again.'
  } = props;
  const [displayPopup, setDisplayPopup] = React.useState<boolean>(false);
  const [file, setFile] = React.useState<any>([]);
  const [progressIndex, setProgressIndex] = React.useState<number>(-1);
  const [thumbs, setThumbs] = React.useState<ThumbsState[]>([]);
  const [uploadedFiles, setUploadedFiles] = React.useState<any>([]);
  const [displayActionPopup, setDisplayActionPopup] = React.useState<boolean>(
    false
  );
  const [selectedIndex, setSelectedIndex] = React.useState<number>(-1);

  let uploadedFileIndex = 0;
  let fileIndex = 0;

  React.useEffect(() => {
    let successIndex = progressIndex;
    let files;

    if (props.status === 'success') {
      files = props.files.map((response: any) => {
        successIndex += 1;
        return Object.assign(response, {
          preview: URL.createObjectURL(response),
          successIndex
        });
      });
      setProgressIndex(successIndex);
    } else {
      files = props.files.map((response: any) => response.file);
    }
    const thumbsObj = files.map((response: any, index: number) => {
      return {
        file: response,
        status: props.status,
        errorMessage: props.status === 'error' ? props.errorMessage[index] : []
      };
    });
    setThumbs(thumb => [...thumb, ...thumbsObj]);
  }, [props.files]);

  const removeFile = (index: number) => {
    const allFiles = thumbs;
    const idx = allFiles.findIndex(
      files => files.file.successIndex >= 0 && files.file.successIndex === index
    );
    allFiles.splice(idx, 1);
    setThumbs(allFiles);
    props.onRemoveFile(
      allFiles
        .filter((f: ThumbsState) => f.status === 'success')
        .map((f: ThumbsState) => f.file)
    );
    setDisplayActionPopup(false);
  };

  const successPreview = (thumb: any) => {
    return (
      <div className="previewContainer">
        <div className="dropzone-upload">
          {thumb.successIndex >= 0 &&
            props.uploadProgress[thumb.successIndex] !== -1 && (
              <ProgressBar
                value={props.uploadProgress[thumb.successIndex]}
                maxProgress={100}
              ></ProgressBar>
            )}
          <div className="dropzone-preview">
            <div className="previewContainer">
              {thumb.successIndex >= 0 &&
                props.uploadProgress[thumb.successIndex] === -1 && (
                  <img alt="preview" className="previewImg" src={invalidImg} />
                )}
              {!(
                thumb.successIndex >= 0 &&
                props.uploadProgress[thumb.successIndex] === -1
              ) &&
                ((thumb.type && !thumb.type.includes('image')) ||
                  (thumb.successIndex === undefined &&
                    thumb.fileExt === '.pdf')) && (
                  <img alt="preview" className="previewImg" src={documentImg} />
                )}
              {((thumb.successIndex === undefined &&
                thumb.fileExt !== '.pdf') ||
                (thumb.successIndex >= 0 &&
                  props.uploadProgress[thumb.successIndex] !== -1 &&
                  thumb.type &&
                  thumb.type.includes('image'))) && (
                <>
                  <img
                    alt="preview"
                    className="previewImg"
                    src={thumb.preview}
                    onClick={() => {
                      setFile(thumb);
                      setDisplayPopup(true);
                    }}
                    onKeyDown={e => {
                      if (e.keyCode === 13 || e.keyCode === 32)
                        setDisplayPopup(true);
                    }}
                    role="presentation"
                  />
                  <img
                    alt="zoom"
                    className="zoomImg"
                    src={zoom}
                    onClick={() => {
                      setFile(thumb);
                      setDisplayPopup(true);
                    }}
                    onKeyDown={e => {
                      if (e.keyCode === 13 || e.keyCode === 32)
                        setDisplayPopup(true);
                    }}
                    role="presentation"
                  />
                </>
              )}
            </div>
          </div>
          <div className="fileInfo">
            {thumb.successIndex >= 0 &&
              props.uploadProgress[thumb.successIndex] === 100 && (
                <span className="date">
                  {props.uploadedOn[thumb.successIndex]}
                </span>
              )}
            {thumb.successIndex === undefined && (
              <span className="date">{thumb.uploadedOn}</span>
            )}
            {props.displayRemoveButton &&
              (thumb.successIndex === undefined ||
                (thumb.successIndex >= 0 &&
                  props.uploadProgress[thumb.successIndex] !== -1)) && (
                <StaticElementButton
                  name="removeButton"
                  id={`remove-button-${thumb.successIndex}`}
                  label="Remove"
                  type="div"
                  onClick={() => {
                    setSelectedIndex(thumb.successIndex);
                    setDisplayActionPopup(true);
                  }}
                  className="remove-button"
                />
              )}
          </div>
        </div>
      </div>
    );
  };

  const successBlock = (thumb: any) => {
    if (props.uploadProgress[thumb.file.successIndex] === 100) {
      return (
        <Card
          style="padding: 8px;" // eslint-disable-line react/style-prop-object
          hat={[{ type: thumb.status, message: props.successMessage }]}
        >
          {' '}
          {/*eslint-disable-line*/}
          {successPreview(thumb.file)}
        </Card>
      );
    }
    if (props.uploadProgress[thumb.file.successIndex] === -1) {
      return (
        <Card
          style="padding: 8px;" // eslint-disable-line react/style-prop-object
          hat={[{ type: 'error', message: systemErrorMessage }]}
        >
          {' '}
          {/*eslint-disable-line*/}
          {successPreview(thumb.file)}
        </Card>
      );
    }
    return (
      <Card
        style="padding: 8px;" // eslint-disable-line react/style-prop-object
      >
        {' '}
        {/*eslint-disable-line*/}
        {successPreview(thumb.file)}
      </Card>
    );
  };

  React.useEffect(() => {
    const thumb = props.uploadedFiles.map((response: any) => {
      return {
        ...response,
        uploadedOn: DateFns.format(
          new Date(response.uploadedOn),
          'MM/dd/yy . h:mm aaa'
        ).toLowerCase(),
        preview: `data:image/${response.fileExt.slice(1)};base64,${
          response.imgData
        }`
      };
    });
    setUploadedFiles(thumb);
  }, [props.uploadedFiles]);

  return (props.files && props.files.length > 0) || uploadedFiles.length > 0 ? (
    <div>
      {displayPopup && (
        <Popup
          modal
          displayCloseButton
          position="screen-center"
          theme="day"
          onClose={() => setDisplayPopup(false)}
        >
          <div className="popupImage">
            <img alt="uploaded" src={file.preview} />
          </div>
        </Popup>
      )}
      {displayActionPopup && (
        <ActionPopup
          headingText={removeFileHeading}
          message={<>{removeFileMsg}</>}
          buttons={[
            {
              label: removeFileNoBtnLbl,
              onClick: () => setDisplayActionPopup(false)
            },
            {
              label: removeFileYesBtnLbl,
              type: 'primary',
              onClick: () => removeFile(selectedIndex)
            }
          ]}
          onPopupClose={() => {
            setDisplayActionPopup(false);
          }}
        />
      )}
      {uploadedFiles.length > 0 &&
        uploadedFiles.map((response: any) => {
          uploadedFileIndex += 1;
          return (
            <div
              key={`${response.sender}_${uploadedFileIndex}`}
              className="dropZone-width"
            >
              <div className="cardSpacing">
                <Card
                  style="padding: 8px;" // eslint-disable-line react/style-prop-object
                >
                  {' '}
                  {/*eslint-disable-line*/}
                  {successPreview(response)}
                </Card>
              </div>
            </div>
          );
        })}
      {thumbs.length > 0 &&
        thumbs.map((thumb: ThumbsState) => {
          fileIndex += 1;
          return thumb.file.preview ? (
            <div
              key={`${thumb.file.name}_${fileIndex}`}
              className="dropZone-width"
            >
              <div className="cardSpacing">{successBlock(thumb)}</div>
            </div>
          ) : (
            <div
              key={`${thumb.file.name}_${fileIndex}`}
              className="dropZone-width"
            >
              <div className="cardSpacing">
                <Card
                  style="padding: 8px;" // eslint-disable-line react/style-prop-object
                  hat={[
                    {
                      type: 'error',
                      message: thumb.errorMessage
                    }
                  ]}
                >
                  {' '}
                  {/*eslint-disable-line*/}
                  <div className="previewContainer">
                    <div className="dropzone-upload">
                      <div className="dropzone-preview">
                        <div className="previewContainer">
                          <img
                            alt="preview"
                            className="previewImg"
                            src={invalidImg}
                          />
                        </div>
                      </div>
                      <div className="fileInfo"></div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          );
        })}
    </div>
  ) : (
    <></>
  );
}
